import { useMemo } from 'react'
import type { CopyKey } from 'locales/copy-keys.gen'
import type { Project } from 'models/project'
import { useProjects } from 'hooks/useProjects'
import { prepareInvestProjects } from 'shared/domain/investOpportunities'
import { buildHeroIdentity } from './useHeroIdentity'
import { useStoProject } from './useStoProject'

export interface ProductSwitcherItem {
  slug: string
  title: string
  ticker?: string
  icons: string[]
  typeLabelKey: CopyKey
  selected: boolean
}

/**
 * The products a visitor can switch to: the Featured assets listing (same filter and order),
 * without the register-interest-only ones, which have no page of their own.
 */
export const toProductSwitcherItems = (
  projects: Project[] | undefined,
  currentId: string | undefined,
): ProductSwitcherItem[] =>
  prepareInvestProjects(projects ?? []).flatMap((project) => {
    const identity = buildHeroIdentity(project)
    if (project.is_static || !project.name || !identity) return []
    return [
      {
        slug: project.name,
        title: identity.title,
        ticker: identity.ticker,
        icons: identity.tickerIcons,
        typeLabelKey: identity.typeLabelKey,
        selected: project._id === currentId,
      },
    ]
  })

/** The project list is already loaded app-wide (AppRouter's slug check), so this reads the cache. */
export function useProductSwitcher(): ProductSwitcherItem[] {
  const { data: projects } = useProjects()
  const { projectId } = useStoProject()
  return useMemo(() => toProductSwitcherItems(projects, projectId), [projects, projectId])
}
